import React from 'react';

/**
 * Helper utility to ensure aria-label attributes have meaningful, non-empty values
 * Provides fallback mechanism when no explicit label is set
 */

/**
 * Validates and provides a fallback aria-label value
 * @param label - The original aria-label value
 * @param fallback - Fallback text to use if label is empty/invalid
 * @param defaultText - Default text to use if both label and fallback are empty
 * @returns A valid aria-label string
 */
export const getValidAriaLabel = (
  label: string | undefined | null,
  fallback?: string | null,
  defaultText: string = 'Link',
): string => {
  // Check if label is valid (non-empty string)
  if (label && typeof label === 'string' && label.trim().length > 0) {
    return label.trim();
  }

  // Check if fallback is valid
  if (fallback && typeof fallback === 'string' && fallback.trim().length > 0) {
    return fallback.trim();
  }

  // Return default text
  return defaultText;
};

/**
 * Validates and provides a fallback for CTA button aria-label
 * @param ariaLabel - The configured aria-label
 * @param buttonLabel - The button text as fallback
 * @returns A valid aria-label string
 */
export const getValidCtaAriaLabel = (
  ariaLabel: string | undefined | null,
  buttonLabel: string | undefined | null,
): string => {
  return getValidAriaLabel(ariaLabel, buttonLabel, 'CTA button');
};

/**
 * Validates and provides a fallback for link aria-label
 * @param title - The link title attribute
 * @param linkText - The link text content as fallback
 * @param href - The link URL for generating a meaningful fallback
 * @returns A valid aria-label string
 */
export const getValidLinkAriaLabel = (
  title: string | undefined | null,
  linkText?: string | null,
  href?: string | null,
): string => {
  // Try to use title first
  if (title && typeof title === 'string' && title.trim().length > 0) {
    return title.trim();
  }

  // Try to use link text
  if (linkText && typeof linkText === 'string' && linkText.trim().length > 0) {
    return linkText.trim();
  }

  // Generate fallback from URL if available
  if (href && typeof href === 'string') {
    try {
      const url = new URL(href);
      const domain = url.hostname.replace('www.', '');
      return `Link to ${domain}`;
    } catch {
      return 'External link';
    }
  }

  return 'Link';
};

interface LinkProps {
  href?: string;
  'aria-label'?: string;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Processes HTML content generated by footnotes to fix empty aria-label attributes
 * @param htmlContent - The HTML content from renderTextWithFootnotesReferencesV2
 * @returns React element with corrected aria-label attributes
 */
export const processFootnoteHTML = (htmlContent: React.ReactNode): React.ReactNode => {
  if (!htmlContent || typeof htmlContent !== 'object') {
    return htmlContent;
  }

  // If it's a React element, check if it needs aria-label correction
  if (React.isValidElement(htmlContent)) {
    const element = htmlContent;

    // Check if it's a link element with empty or missing aria-label
    if (element.type === 'a') {
      const props = element.props as LinkProps;
      const currentAriaLabel = props['aria-label'];

      // If aria-label is empty or missing, generate a fallback
      if (currentAriaLabel === '' || currentAriaLabel === null || currentAriaLabel === undefined) {
        const href = props.href || '';
        const linkText = props.children || '';
        const className = props.className || '';

        // Check if this is a footnote reference link
        const isFootnoteLink = className.includes('footnote') || href.startsWith('#');

        if (isFootnoteLink) {
          // Generate appropriate aria-label for footnote
          let newAriaLabel = 'Footnote reference';

          // If there's text content, use it
          if (typeof linkText === 'string' && linkText.trim()) {
            newAriaLabel = `Footnote: ${linkText.trim()}`;
          } else if (href.startsWith('#')) {
            // Extract footnote ID from href if it's a footnote link and no text available
            const footnoteId = href.substring(1);
            newAriaLabel = `Footnote reference ${footnoteId}`;
          }

          // Clone element with corrected aria-label
          return React.cloneElement(element, {
            ...props,
            'aria-label': newAriaLabel,
          } as React.Attributes);
        }
      }
    }

    // If element has children, process them recursively
    if (element.props && typeof element.props === 'object' && 'children' in element.props) {
      const props = element.props as { children?: React.ReactNode };
      const processedChildren = Array.isArray(props.children)
        ? props.children.map(processFootnoteHTML)
        : processFootnoteHTML(props.children);

      return React.cloneElement(element, {
        ...element.props,
        children: processedChildren,
      } as React.Attributes);
    }
  }

  // If it's an array of elements, process each one
  if (Array.isArray(htmlContent)) {
    return htmlContent.map(processFootnoteHTML);
  }

  return htmlContent;
};
